import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import mixpanel from "mixpanel-browser";

import { transferLegacyCredits } from "store/parametricAnalysis/thunk";

import Button from "components/Button";

import classes from "./styles.module.scss";
import parentClasses from "../styles.module.scss";

const formatCount = (value) => Number(value || 0).toLocaleString();

const LegacyCredits = ({
    legacyTotalCredits = 0,
    onClose,
    transferLegacyCredits,
    parametricId,
    userUid,
    shortfallRuns = 0,
    availableRuns = 0,
    onRunsToUnlockChange,
}) => {
    const defaultAmount = Math.min(Math.max(shortfallRuns, 0), legacyTotalCredits);
    const [amountToTransfer, setAmountToTransfer] = useState(defaultAmount);
    const [isTransferring, setIsTransferring] = useState(false);
    const [transferError, setTransferError] = useState(null);

    useEffect(() => {
        setAmountToTransfer(Math.min(Math.max(shortfallRuns, 0), legacyTotalCredits));
    }, [shortfallRuns, legacyTotalCredits]);

    useEffect(() => {
        onRunsToUnlockChange?.(amountToTransfer);
    }, [amountToTransfer, onRunsToUnlockChange]);

    const sliderProgress =
        legacyTotalCredits <= 0
            ? 0
            : amountToTransfer < legacyTotalCredits * 0.8
              ? (amountToTransfer / legacyTotalCredits) * 100 + 0.5
              : (amountToTransfer / legacyTotalCredits) * 100 - 1;

    const projectedUnlocked = availableRuns + amountToTransfer;
    const hasEnoughLegacy = legacyTotalCredits >= shortfallRuns;
    const isOverMinimum = shortfallRuns > 0 && amountToTransfer > shortfallRuns;
    const alreadyCovered = shortfallRuns === 0;

    const summary = useMemo(() => {
        if (alreadyCovered) {
            return (
                <>
                    This analysis already has enough unlocked runs. Legacy credits convert 1:1 into unlocked runs if you
                    want extra capacity.
                </>
            );
        }

        return (
            <>
                You need <strong>{formatCount(shortfallRuns)}</strong> more unlocked run
                {shortfallRuns === 1 ? "" : "s"} for this analysis. Legacy credits convert <strong>1:1</strong>.
            </>
        );
    }, [alreadyCovered, shortfallRuns]);

    const handleTransfer = async (e) => {
        e.preventDefault();

        try {
            setIsTransferring(true);
            setTransferError(null);
            await transferLegacyCredits(parametricId, userUid, amountToTransfer);
            mixpanel.track("Parametric Runs Unlock", {
                UserId: userUid,
                AnalysisId: parametricId,
                Credits: amountToTransfer,
                CreditType: "legacy",
                Runs: amountToTransfer,
            });
            onClose();
            setAmountToTransfer(0);
        } catch (error) {
            setTransferError(error?.message || "Failed to unlock runs with legacy credits");
        } finally {
            setIsTransferring(false);
        }
    };

    return (
        <div className={classes.legacyCreditsContainer}>
            <div className={classes.legacyCreditsBody}>
                <p className={classes.summaryText}>{summary}</p>
                <p className={classes.rateText}>
                    After unlock: <strong>{formatCount(projectedUnlocked)}</strong> unlocked runs
                </p>

                <div className={classes.sliderContainer}>
                    <div style={{ width: "100%", position: "relative" }}>
                        <input
                            className={classes.rangeInput}
                            type="range"
                            min={0}
                            max={legacyTotalCredits}
                            value={amountToTransfer}
                            disabled={legacyTotalCredits <= 0 || isTransferring}
                            onChange={(e) => setAmountToTransfer(+e.target.value)}
                            style={{
                                width: "100%",
                                background: `linear-gradient(to right, #003ba0 0%, #003ba0 ${sliderProgress}%, #f0f9ff ${sliderProgress}%, #f0f9ff 100%)`,
                            }}
                        />
                        {legacyTotalCredits > 0 && (
                            <span
                                className={classes.amountSpan}
                                style={{
                                    left: `calc(${(amountToTransfer / legacyTotalCredits) * 100}% - ${
                                        (amountToTransfer / legacyTotalCredits) * 20
                                    }px)`,
                                    transform: "none",
                                }}
                            >
                                {formatCount(amountToTransfer)}
                            </span>
                        )}
                    </div>
                    <div className={classes.sliderInfoContainer}>
                        <span>0</span>
                        <span>{formatCount(legacyTotalCredits)}</span>
                    </div>
                </div>

                {isOverMinimum && (
                    <p className={`${parentClasses.alert} ${parentClasses.alertWarning}`}>
                        You&apos;re unlocking more runs than this analysis currently needs. That&apos;s fine if you plan
                        to add more upgrades later.
                    </p>
                )}

                {!hasEnoughLegacy && shortfallRuns > 0 && (
                    <p className={`${parentClasses.alert} ${parentClasses.alertError}`}>
                        You need <strong>{formatCount(shortfallRuns)}</strong> unlocked runs for this analysis, but you
                        only have <strong>{formatCount(legacyTotalCredits)}</strong> legacy credit
                        {legacyTotalCredits === 1 ? "" : "s"}. Switch to credits or buy more.
                    </p>
                )}

                {transferError && <p className={classes.errorText}>{transferError}</p>}
            </div>

            <div className={classes.legacyCreditsFooter}>
                <Button type="red" onClick={onClose} disabled={isTransferring}>
                    Cancel
                </Button>
                <Button
                    onClick={handleTransfer}
                    disabled={isTransferring || amountToTransfer <= 0 || amountToTransfer > legacyTotalCredits}
                >
                    {isTransferring
                        ? "Unlocking..."
                        : `Spend ${formatCount(amountToTransfer)} legacy credit${amountToTransfer === 1 ? "" : "s"}`}
                </Button>
            </div>
        </div>
    );
};

const mapDispatchToProps = (dispatch) => ({
    transferLegacyCredits: (analysisId, userUid, creditsToTransfer) =>
        dispatch(transferLegacyCredits(analysisId, userUid, creditsToTransfer)),
});

export default connect(null, mapDispatchToProps)(LegacyCredits);
